import { Box, Card, CardContent, Skeleton, Typography } from '@mui/joy';

import { Color, Direction, Level, Size, SkeletonVariant, Variant } from 'theme/enums';

interface IProps {
  value: number | string;
  secondaryValue?: string;
  valueColor?: Color;
  isLoading?: boolean;
  isCompact?: boolean;
  label: string;
}

export const StatCard = ({
  isLoading,
  isCompact,
  label,
  secondaryValue,
  value,
  valueColor,
}: IProps) => {
  // Compact trades the stacked label/value block for a single line, so a row of
  // them takes roughly a third less height and fits more cards across before
  // wrapping. Used where the cards sit above a table that wants the space.
  const cardProps = {
    variant: Variant.OUTLINED,
    size: isCompact ? Size.SM : Size.MD,
    sx: { boxShadow: Size.SM, height: '100%' },
  };

  if (isLoading) {
    return (
      <Card {...cardProps}>
        <CardContent>
          <Skeleton
            sx={{ height: isCompact ? 16 : 20, width: '40%' }}
            variant={SkeletonVariant.TEXT}
          />
          <Skeleton
            sx={{ height: isCompact ? 20 : 32, width: '60%' }}
            variant={SkeletonVariant.TEXT}
          />
          <Skeleton
            sx={{ height: isCompact ? 16 : 20, width: '80%' }}
            variant={SkeletonVariant.TEXT}
          />
        </CardContent>
      </Card>
    );
  }

  if (isCompact) {
    return (
      <Card {...cardProps}>
        <CardContent>
          {/* Label and value share a line while they fit, and fall back to
              stacking when the card is too narrow for both — so a wide screen
              gets the short card and a laptop still gets the full label rather
              than an ellipsis. space-between right-aligns the value on a shared
              line and leaves it at the start once wrapped. */}
          <Box
            sx={{
              alignItems: 'baseline',
              columnGap: 1,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
              {label}
            </Typography>
            <Typography level={Level.H4} color={valueColor}>
              {value}
            </Typography>
          </Box>
          {secondaryValue && <Typography level={Level.BODY_XS}>{secondaryValue}</Typography>}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card {...cardProps}>
      <CardContent>
        <Typography color={Color.NEUTRAL}>{label}</Typography>
        <Box
          sx={{
            alignItems: { md: 'flex-end', xs: 'flex-start' },
            display: 'flex',
            flexDirection: { md: Direction.ROW, xs: Direction.COLUMN },
            justifyContent: 'space-between',
          }}
        >
          <Typography level={Level.H2} color={valueColor}>
            {value}
          </Typography>
          {secondaryValue && <Typography level={Level.BODY_SM}>{secondaryValue}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
};
